import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { login } from "../../services/authService";
import AuthShell from "./AuthShell";
import { beginMicrosoftSignIn, getPublicAuthMode } from "./authAccess";
import "./Auth.css";

const microsoftHighlights = [
  {
    label: "Island identity",
    text: "Use the same Island Millwork Microsoft account trusted across your org-managed tools.",
  },
  {
    label: "Estimator-ready",
    text: "Public access stays scoped to estimator workflows while downstream routing and roles stay managed centrally.",
  },
  {
    label: "No local signup",
    text: "Self-serve registration is hidden whenever Microsoft-managed public auth is enabled.",
  },
];

const localDevHighlights = [
  {
    label: "Development fallback",
    text: "Local username and password sign-in remains available for local workspace testing.",
  },
  {
    label: "Estimator-focused",
    text: "Self-serve development registration creates estimator accounts only.",
  },
  {
    label: "Routing-safe",
    text: "Auth navigation stays inside the React app without plain links or manual reloads.",
  },
];

const LoginForm = () => {
  const authMode = getPublicAuthMode();
  const location = useLocation();
  const navigate = useNavigate();
  const { updateCurrentUser } = useAuth();

  // Dev-only convenience: local builds may bake in fallback credentials so the
  // form comes prefilled (REACT_APP_DEV_LOGIN_* are unset in production builds).
  const [formData, setFormData] = useState({
    username: process.env.REACT_APP_DEV_LOGIN_EMAIL || "",
    password: process.env.REACT_APP_DEV_LOGIN_PASSWORD || "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const registrationNotice = location.state?.notice || "";

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await login(formData);
      if (response?.user) {
        updateCurrentUser(response.user);
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message || "Unable to sign in with local credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError("");
    setIsMicrosoftLoading(true);

    try {
      await beginMicrosoftSignIn();
    } catch (err) {
      setError(err?.message || "Microsoft sign-in could not be started.");
      setIsMicrosoftLoading(false);
    }
  };

  const renderCardContent = () => {
    if (authMode.canStartMicrosoftSignIn) {
      return (
        <>
          {registrationNotice ? (
            <div className="auth-success">{registrationNotice}</div>
          ) : null}
          {error ? <div className="auth-error">{error}</div> : null}

          <button
            type="button"
            className="auth-button auth-button--primary auth-button--microsoft"
            onClick={handleMicrosoftLogin}
            disabled={isMicrosoftLoading}
          >
            {isMicrosoftLoading
              ? "Redirecting to Microsoft..."
              : "Continue with Microsoft"}
          </button>

          <p className="auth-helper-text">
            Sign in with your Island Millwork Microsoft account. Public username
            and password sign-in is disabled in this workspace.
          </p>
        </>
      );
    }

    if (authMode.canUseLocalCredentials) {
      return (
        <>
          {registrationNotice ? (
            <div className="auth-success">{registrationNotice}</div>
          ) : null}
          {error ? <div className="auth-error">{error}</div> : null}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label htmlFor="username" className="auth-label">
                Username or email
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={formData.username}
                onChange={handleChange}
                className="auth-input"
                required
              />
            </div>

            <div className="auth-field">
              <label htmlFor="password" className="auth-label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                className="auth-input"
                required
              />
            </div>

            <button
              type="submit"
              className="auth-button auth-button--primary"
              disabled={isLoading}
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="auth-links">
            <span>Need a local development account?</span>
            <Link to="/register" className="auth-inline-link">
              Create one
            </Link>
          </div>
        </>
      );
    }

    return (
      <>
        {error ? <div className="auth-error">{error}</div> : null}
        <div className="auth-message-card">
          <p className="auth-message-card__title">
            Microsoft access is expected here
          </p>
          <p className="auth-message-card__body">
            Password sign-in is not available outside development. Configure
            Microsoft access for this environment or return to a local dev
            workspace.
          </p>
        </div>
      </>
    );
  };

  const shellProps = authMode.canStartMicrosoftSignIn
    ? {
        eyebrow: "Island Millwork Access",
        title: "Sign in with your Island Millwork Microsoft account",
        description:
          "CostPilot uses Microsoft identity as the primary public sign-in path whenever the workspace is configured for org-managed access.",
        helper:
          "Use the same company account you rely on across Island systems. Public self-serve registration stays hidden in this mode.",
        highlights: microsoftHighlights,
      }
    : authMode.canUseLocalCredentials
      ? {
          eyebrow: "Local Development Access",
          title: "Sign in with your local development account",
          description:
            "This fallback keeps local estimator testing moving when Microsoft auth is not configured for the workspace.",
          helper:
            "Local credentials are a development-only path. Production access should flow through Microsoft-managed identity.",
          highlights: localDevHighlights,
          asideTitle:
            "Development fallback without breaking the org-first model.",
        }
      : {
          eyebrow: "Provisioned Access",
          title: "Microsoft sign-in is required for this workspace",
          description:
            "This deployment is not exposing public password sign-in. Access should be provisioned through Island Millwork Microsoft identity.",
          helper:
            "If you expected local login here, use a local development workspace instead.",
          highlights: microsoftHighlights,
        };

  return (
    <AuthShell {...shellProps} statusLabel={authMode.statusLabel}>
      {renderCardContent()}
    </AuthShell>
  );
};

export default LoginForm;
